export interface RosterEntry {
  id: number;
  userId: number;
  name: string;
  email: string;
  chapter: string;
  chapterId: number;
  onboarded: string;
  autoRenew: boolean;
  status: string;
  permissionLevel: number;
  officerRoles: { id: number; name: string }[];
}

export interface InviteEntry {
  id: number;
  email: string;
  chapter: string;
  chapterId: number;
  year: number;
  invitedBy: string;
  invitedAt: string;
  status: string;
  officerRoleIds: number[];
}

export interface InviteResult {
  email: string;
  status: string;
  error?: string;
}

export interface ActivityEntry {
  id: number;
  action_by: string;
  target_name: string | null;
  table: string;
  column: string;
  previous_value: string | null;
  new_value: string | null;
  chapter_id: number | null;
  timestamp: string;
}

const headers = { 'Content-Type': 'application/json' };
const opts: RequestInit = { credentials: 'include', headers };

export async function getRoster(): Promise<RosterEntry[]> {
  const res = await fetch('/api/officer_management/roster', opts);
  if (!res.ok) throw new Error(`Failed to fetch roster: ${res.status}`);
  return res.json();
}

export async function toggleAutoRenew(id: number, auto_renew: boolean): Promise<{ id: number; auto_renew: boolean }> {
  const res = await fetch(`/api/officer_management/auto_renew/${id}`, {
    ...opts,
    method: 'PUT',
    body: JSON.stringify({ auto_renew }),
  });
  if (!res.ok) throw new Error(`Failed to toggle auto-renew: ${res.status}`);
  return res.json();
}

export async function removeOfficer(id: number, comment: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`/api/officer_management/remove/${id}`, {
    ...opts,
    method: 'DELETE',
    body: JSON.stringify({ comment }),
  });
  if (!res.ok) throw new Error(`Failed to remove officer: ${res.status}`);
  return res.json();
}

export async function sendInvites(
  emails: string[],
  chapter_id: number,
  officer_role_ids: number[]
): Promise<InviteResult[]> {
  const res = await fetch('/api/officer_management/invite', {
    ...opts,
    method: 'POST',
    body: JSON.stringify({ emails, chapter_id, officer_role_ids }),
  });
  if (!res.ok) throw new Error(`Failed to send invites: ${res.status}`);
  return res.json();
}

export async function getInvites(): Promise<InviteEntry[]> {
  const res = await fetch('/api/officer_management/invites', opts);
  if (!res.ok) throw new Error(`Failed to fetch invites: ${res.status}`);
  return res.json();
}

export async function cancelInvite(id: number): Promise<{ success: boolean }> {
  const res = await fetch(`/api/officer_management/invites/${id}`, {
    ...opts,
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to cancel invite: ${res.status}`);
  return res.json();
}

export async function getActivity(): Promise<ActivityEntry[]> {
  const res = await fetch('/api/officer_management/activity', opts);
  if (!res.ok) throw new Error(`Failed to fetch activity: ${res.status}`);
  return res.json();
}
